import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../lib/api';
import { formatCurrency, formatRelativeTime, formatDateTime } from '../../lib/utils';
import { StatusBadge } from '../ui/Badge';

interface ConversionEvent {
  id: number;
  conversionId: number;
  txid: string;
  eventType: string;
  revenue: number;
  payout: number;
  status: string;
  createdAt: number;
}

export function ConversionEventsPanel({ conversionId }: { conversionId: number }) {
  const events = useQuery({
    queryKey: ['conversion-events', conversionId],
    queryFn: () =>
      apiClient.get<ConversionEvent[]>(
        `/api/v1/conversions/${conversionId}/events`,
      ),
    staleTime: 30_000,
  });

  return (
    <tr className="bg-bg-primary/60 border-l-4 border-neon-magenta/40 pl-1">
      <td colSpan={9} className="px-6 py-3">
        {events.isLoading ? (
          <p className="font-mono text-[11px] text-text-tertiary">loading events...</p>
        ) : (events.data?.length ?? 0) === 0 ? (
          <p className="font-mono text-[11px] text-text-tertiary">
            no additional events (single-event conversion)
          </p>
        ) : (
          <div className="space-y-1.5">
            <p className="font-display text-[10px] uppercase tracking-wider text-text-secondary mb-2">
              Event history · {events.data!.length} event(s)
            </p>
            {events.data!.map((ev) => (
              <div
                key={ev.id}
                className="flex flex-wrap items-center gap-x-4 gap-y-1 font-mono text-[11px] border-l-2 border-neon-magenta/40 pl-3 py-0.5"
              >
                <span className="text-text-tertiary" title={formatDateTime(ev.createdAt)}>
                  {formatRelativeTime(ev.createdAt)}
                </span>
                <span className="text-neon-cyan uppercase tracking-wider">{ev.eventType}</span>
                <span className="text-text-secondary truncate max-w-[220px]" title={ev.txid}>
                  txid: {ev.txid}
                </span>
                <span className="numeric text-neon-yellow">{formatCurrency(ev.revenue)}</span>
                <span className="numeric text-text-secondary">payout {formatCurrency(ev.payout)}</span>
                <StatusBadge status={ev.status} />
              </div>
            ))}
          </div>
        )}
      </td>
    </tr>
  );
}
